/**
 * Página MOBILE "PLD" (Task #31 Fase 3). READ-ONLY.
 *
 * PLD médio do mês (realizado MTD + projeção de fechamento, banda P10–P90) +
 * mini visão da projeção dos próximos dias. SEM entrada de DECOMP (desktop-only).
 * Reusa usePldProjecao (mesma fonte do gráfico da Visão Geral).
 */
import { useMemo } from "react";
import { usePldProjecao } from "../../hooks/usePldProjecao";
import { Card, MobileHeader, Stat } from "./mobileUi";
import { fmtPld, mesLabel, mesRefCorrente } from "../mesa/pld/pldShared";

const COR_PROJECAO = "#F5A623";

function diaCurto(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

export function MobilePld({ onLogout, onRefresh }: { onLogout: () => void; onRefresh?: () => void }) {
  const mesRef = mesRefCorrente();
  const pld = usePldProjecao(mesRef, "SE");

  const dias = pld.data?.dias ?? [];
  const realizados = dias.filter((d) => d.realizado && d.pld_projetado != null);
  const pldMtd = realizados.length
    ? realizados.reduce((s, d) => s + (d.pld_projetado as number), 0) / realizados.length
    : null;
  const fech = pld.data?.fechamento_projetado ?? null;
  const p10 = pld.data?.fechamento_p10 ?? null;
  const p90 = pld.data?.fechamento_p90 ?? null;
  const versao = pld.data?.modelo_versao ?? "";

  // Mini projeção: próximos dias ainda NÃO realizados (com valor projetado).
  const futuros = useMemo(
    () => dias.filter((d) => !d.realizado && d.pld_projetado != null).slice(0, 8),
    [dias],
  );

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-pld">
      <MobileHeader subtitle="PLD" onLogout={onLogout} onRefresh={onRefresh} />
      <main className="px-4 py-4 flex flex-col gap-4 max-w-md mx-auto">
        <Card title={`PLD SE — ${mesLabel(mesRef)}`} testId="mobile-pld-mes">
          <Stat label="Realizado (média até hoje)" tone="turq"
            value={pldMtd != null ? fmtPld(pldMtd) : "—"} testId="mobile-pld-mtd" />
          <div className="border-t border-white/10 mt-1 pt-1">
            <Stat label="Projeção de fechamento" small
              value={fech != null ? fmtPld(fech) : "—"} testId="mobile-pld-fechamento" />
            <div className="text-white/40 text-[11px] mt-1" data-testid="mobile-pld-banda">
              {p10 != null && p90 != null
                ? `Banda P10–P90: ${fmtPld(p10)} – ${fmtPld(p90)}`
                : "Sem projeção de fechamento."}
            </div>
          </div>
        </Card>

        <Card title="Projeção dos próximos dias" testId="mobile-pld-projecao">
          {futuros.length === 0 ? (
            <div className="text-white/40 text-sm" data-testid="mobile-pld-projecao-vazio">
              Sem dias projetados (mês já fechado ou sem âncora DECOMP).
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-white/5">
              {futuros.map((d) => (
                <div key={d.data} className="flex items-center justify-between gap-3 py-1.5" data-testid="mobile-pld-dia-row">
                  <span className="text-sm text-white/70">{diaCurto(d.data)}</span>
                  <span className="text-sm font-mono italic" style={{ color: COR_PROJECAO }}>
                    {fmtPld(d.pld_projetado)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="text-white/40 text-[11px] mt-2">
            Projeção {versao || "v1.x"} (estimativa). Entrada de DECOMP e o gráfico
            completo no desktop.
          </p>
        </Card>
      </main>
    </div>
  );
}
